/**
 * Lord of Venues - Verto-Inspired Palette
 *
 * Aesthetic: "Clean Fintech"
 * Light mode with monospace typography and clean data visualization
 */

export const palette = {
  mode: 'light' as const,

  primary: {
    main: '#1A1A1A',        // Near black - primary actions
    light: '#404040',       // Lighter black
    dark: '#0F0F0F',        // Pure black
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: '#6B8AFB',        // Soft blue - data visualization
    light: '#8BA4FC',       // Light blue
    dark: '#4B6ADB',        // Deep blue
    contrastText: '#FFFFFF',
  },

  background: {
    default: '#F5F5F3',     // Warm light gray - main background
    paper: '#FFFFFF',       // Pure white - cards
  },

  // Extended surface colors for modern UI
  surface: {
    glass: 'rgba(255, 255, 255, 0.9)',
    glassLight: 'rgba(255, 255, 255, 0.7)',
    glassBorder: 'rgba(0, 0, 0, 0.08)',
    hover: 'rgba(0, 0, 0, 0.04)',
  },

  text: {
    primary: '#0F0F0F',     // Deeper black for stronger contrast
    secondary: '#4B5563',   // Darker gray - better contrast (WCAG AAA)
    disabled: '#9CA3AF',
  },

  divider: 'rgba(0, 0, 0, 0.08)',

  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#1A1A1A',
  },

  success: {
    main: '#22C55E',        // Verto green
    light: '#4ADE80',
    dark: '#16A34A',
    contrastText: '#FFFFFF',
  },

  info: {
    main: '#6B8AFB',        // Soft blue
    light: '#8BA4FC',
    dark: '#4B6ADB',
    contrastText: '#FFFFFF',
  },

  // Accent colors for Verto style
  accent: {
    green: '#22C55E',       // Success/positive indicators
    blue: '#6B8AFB',        // Data visualization primary
    blueLight: '#A5B4FC',   // Data visualization secondary
    purple: '#A78BFA',      // Tertiary accent
  },

  // Venue colors (darker variants for WCAG AA contrast on light background)
  venue: {
    gaming: '#7C3AED',      // Darker purple for better contrast (5.2:1)
    snooker: '#047857',     // Darker emerald green for better contrast (≥4.5:1)
    kids: '#C2410C',        // Darker orange for better contrast (≥4.5:1)
  },

  // Gradient definitions
  gradients: {
    primary: 'linear-gradient(135deg, #0F0F0F 0%, #404040 100%)',
    secondary: 'linear-gradient(135deg, #6B8AFB 0%, #4B6ADB 100%)',
    hero: 'linear-gradient(180deg, #FAF9F7 0%, #F0EDFF 50%, #E8F4F8 100%)',
    heroHeadline: 'linear-gradient(135deg, #0F0F0F 0%, #A855F7 50%, #6B8AFB 100%)',
    buttonPrimary: 'linear-gradient(135deg, #1A1A1A 0%, #404040 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 100%)',
    card: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
    glow: 'radial-gradient(ellipse at center, rgba(107, 138, 251, 0.1) 0%, transparent 70%)',
    vibrantGlow: 'radial-gradient(ellipse at center, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
    text: 'linear-gradient(135deg, #0F0F0F 0%, #404040 100%)',
  },
};

// Dark mode palette
export const darkPalette = {
  mode: 'dark' as const,

  primary: {
    main: '#FFFFFF',        // White - primary actions in dark mode
    light: '#F5F5F5',       // Off-white
    dark: '#E5E5E5',        // Light gray
    contrastText: '#1A1A1A',
  },

  secondary: {
    main: '#8BA4FC',        // Bright blue - more visible in dark
    light: '#A5B4FC',       // Lighter blue
    dark: '#6B8AFB',        // Soft blue
    contrastText: '#1A1A1A',
  },

  background: {
    default: '#0A0A0A',     // Near black - main background
    paper: '#1A1A1A',       // Dark gray - cards
  },

  // Extended surface colors for dark mode
  surface: {
    glass: 'rgba(26, 26, 26, 0.9)',
    glassLight: 'rgba(26, 26, 26, 0.7)',
    glassBorder: 'rgba(255, 255, 255, 0.08)',
    hover: 'rgba(255, 255, 255, 0.04)',
  },

  text: {
    primary: '#F5F5F5',     // Off-white
    secondary: '#9CA3AF',   // Light gray
    disabled: '#6B7280',
  },

  divider: 'rgba(255, 255, 255, 0.08)',

  error: {
    main: '#F87171',        // Lighter red for dark mode
    light: '#FCA5A5',
    dark: '#EF4444',
    contrastText: '#1A1A1A',
  },

  warning: {
    main: '#FBBF24',        // Brighter yellow
    light: '#FDE047',
    dark: '#F59E0B',
    contrastText: '#1A1A1A',
  },

  success: {
    main: '#4ADE80',        // Brighter green
    light: '#86EFAC',
    dark: '#22C55E',
    contrastText: '#1A1A1A',
  },

  info: {
    main: '#8BA4FC',        // Bright blue
    light: '#A5B4FC',
    dark: '#6B8AFB',
    contrastText: '#1A1A1A',
  },

  // Accent colors for dark mode
  accent: {
    green: '#4ADE80',       // Brighter green
    blue: '#8BA4FC',        // Brighter blue
    blueLight: '#A5B4FC',   // Light blue
    purple: '#C4B5FD',      // Lighter purple
  },

  // Venue colors (vibrant variants for visual appeal on dark background - already AAA)
  venue: {
    gaming: '#A855F7',      // Vibrant purple (5.0:1 - AAA)
    snooker: '#10B981',     // Vibrant green (7.8:1 - AAA)
    kids: '#FB923C',        // Vibrant orange (8.8:1 - AAA)
  },

  // Gradient definitions for dark mode
  gradients: {
    primary: 'linear-gradient(135deg, #FFFFFF 0%, #E5E5E5 100%)',
    secondary: 'linear-gradient(135deg, #8BA4FC 0%, #6B8AFB 100%)',
    hero: 'linear-gradient(180deg, #0A0A0A 0%, #1A1A1A 100%)',
    heroHeadline: 'linear-gradient(135deg, #FFFFFF 0%, #C4B5FD 50%, #8BA4FC 100%)',
    buttonPrimary: 'linear-gradient(135deg, #FFFFFF 0%, #E5E5E5 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #E5E5E5 0%, #D5D5D5 100%)',
    card: 'linear-gradient(180deg, #1A1A1A 0%, #0F0F0F 100%)',
    glow: 'radial-gradient(ellipse at center, rgba(139, 164, 252, 0.1) 0%, transparent 70%)',
    text: 'linear-gradient(135deg, #FFFFFF 0%, #E5E5E5 100%)',
  },
};

// CSS variables for use with Tailwind and custom styles
export const cssVariables = {
  '--color-primary': palette.primary.main,
  '--color-primary-light': palette.primary.light,
  '--color-primary-dark': palette.primary.dark,
  '--color-secondary': palette.secondary.main,
  '--color-background': palette.background.default,
  '--color-surface': palette.background.paper,
  '--color-text-primary': palette.text.primary,
  '--color-text-secondary': palette.text.secondary,
  '--color-accent-green': '#22C55E',
  '--color-accent-blue': '#6B8AFB',
  '--glass-bg': palette.surface.glass,
  '--glass-border': palette.surface.glassBorder,
};
